import React from "react";
import { Button, Card, Descriptions, Image, Modal, Space, Table, Typography } from "antd";
import { observer } from "mobx-react";
import { userActivityStore } from "../../store/activity/UserActivityStore";
import { fmtDateTime, fmtDateTime1, fmtDateTime2, fmtMoney2 } from "../../common/Utils";
import moment from "moment-timezone";
import { UserLink } from "../../components/UserLink/UserLink";
import { EduShareOfferBannerDlg } from "../banner/EduShareOfferBannerDlg";
import { bannerEditStore } from "../../store/content/banner/BannerEditStore";

export const UserActivityDlg = observer(() => {
  const {
    category,
    title,
    CreatedAt,
    UpdatedAt,
    Status,
    Notes,
    ShareDetail,
    Recipients,
    CostAndSave,
    producer,
    consumerChildren,
    services,
    banner,
    OrderId,
  } = userActivityStore.data || {};
  const amount = userActivityStore.data?.amount
    ? currency(userActivityStore.data?.amount / 100.0, { precision: 2 }).format()
    : "-";
  const recipients = [
    ...(Recipients?.community || []).map((x) => ({ ...x, type: "community" })),
    ...(Recipients?.group || []).map((x) => ({ ...x, type: "group" })),
    ...(Recipients?.friend || []).map((x) => {
      const [name] = x.name.split(" (");
      return { ...x, name, type: "friend" };
    }),
  ].map((f, i) => {
    if (f.type === "friend") {
      return (
        <span>
          {i === 0 ? "" : ", "}
          <UserLink userId={f.id} name={f.name}></UserLink>
        </span>
      );
    } else {
      return i === 0 ? f.name : `, ${f.name}`;
    }
  });

  const columns = [
    { title: "Date", key: "date", render: (t, r) => fmtDateTime1(r.StartTime, r.Timezone) },
    { title: "Status", key: "status", dataIndex: "Status", render: (t) => t.split("#")[0] },
    { title: "Cost", key: "cost", dataIndex: "Cost" },
    { title: "Consumer Rate", key: "cRate", dataIndex: ["Consumer", "rate"] },
    { title: "Producer Rate", key: "pRate", dataIndex: ["Producer", "rate"] },
  ];

  const uShareOfferColumns = [
    { title: "Item # ", key: "i", render: (t, r, i) => i + 1 },
    {
      title: "Pic",
      key: "pic",
      render: (t, r) =>
        r.pic?.thumbnail ? (
          <Image
            style={{ maxWidth: 120, maxHeight: 120 }}
            src={`https://${process.env.REACT_APP_CLOUDFRONT}.cloudfront.net/${r.pic?.thumbnail}`}
          ></Image>
        ) : null,
    },
    { title: "Description", key: "description", dataIndex: "description" },
    {
      title: "Fare",
      key: "fare",
      render: (t, r) => `${fmtMoney2(r.fare, "", r.fareType === "perHour" ? "/Hour" : "/Item")}`,
    },
  ];

  const done = [
    "Carpool Request",
    "Childcare Request",
    "uShare Request",
    "uShare Offer",
    "Carpool Offer",
    "eduShare Offer",
  ];

  return (
    <Modal
      visible={userActivityStore.visible}
      title={title}
      onOk={() => userActivityStore.close()}
      onCancel={() => userActivityStore.close()}
      width={800}
    >
      <Card loading={userActivityStore.loading} bordered={false} bodyStyle={{ padding: 0 }}>
        {category === "Carpool Request" && (
          <div>
            <Descriptions column={2}>
              <Descriptions.Item label="Category">{category}</Descriptions.Item>

              <Descriptions.Item label="Sender">
                {userActivityStore.data?.sender && (
                  <UserLink
                    userId={userActivityStore.data?.sender?.id}
                    name={userActivityStore.data?.sender?.name}
                  ></UserLink>
                )}
              </Descriptions.Item>

              <Descriptions.Item label="Date Posted">{fmtDateTime(CreatedAt)}</Descriptions.Item>

              <Descriptions.Item label="Amount">
                {fmtMoney2(CostAndSave?.cost?.per || CostAndSave?.cost?.total, "-", "/trip")}
              </Descriptions.Item>
            </Descriptions>

            <Descriptions column={1}>
              <Descriptions.Item label="Status & Time of Status Change">
                {Status}, {fmtDateTime(UpdatedAt)}
              </Descriptions.Item>
              <Descriptions.Item label="One Time / Repeat">
                {ShareDetail.schedule.length > 1 ? "Repeat" : "One Time"}
              </Descriptions.Item>
              <Descriptions.Item label="Date">
                {ShareDetail.schedule.map((d) => moment(d).format("M/D")).join(", ")}
              </Descriptions.Item>
              <Descriptions.Item label="One way / Round trip"> {ShareDetail.type} </Descriptions.Item>
              <Descriptions.Item label="Pick up Time">
                {moment(ShareDetail.pickUpTime, "HH:mm").format("h:mma")}
              </Descriptions.Item>
              {ShareDetail.type === "Round Trip" && (
                <Descriptions.Item label="Time To Return">
                  {moment(ShareDetail.timeToReturn, "HH:mm").format("h:mma")}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Pick up Address"> {ShareDetail?.pickUpPlace?.displayAddress}</Descriptions.Item>
              <Descriptions.Item label="Drop off Address">
                {ShareDetail?.dropOffPlace?.displayAddress}{" "}
              </Descriptions.Item>
              <Descriptions.Item label="Participating Children">
                {consumerChildren?.map(({ name }) => name).join(", ")}
              </Descriptions.Item>
              <Descriptions.Item label="Sender Notes"> {Notes}</Descriptions.Item>
              <Descriptions.Item label="Post Recipients">{recipients}</Descriptions.Item>
              <Descriptions.Item label="Counterparty">
                {producer && <UserLink userId={producer?.id} name={producer?.name}></UserLink>}
              </Descriptions.Item>
            </Descriptions>

            {services.length > 0 && (
              <div>
                <Descriptions title="Services"></Descriptions>
                <Table columns={columns} dataSource={services} pagination={false}></Table>
              </div>
            )}
          </div>
        )}

        {category === "Childcare Request" && (
          <div>
            <Descriptions column={2}>
              <Descriptions.Item label="Category">{category}</Descriptions.Item>

              <Descriptions.Item label="Sender">
                {userActivityStore.data?.sender && (
                  <UserLink
                    userId={userActivityStore.data?.sender?.id}
                    name={userActivityStore.data?.sender?.name}
                  ></UserLink>
                )}
              </Descriptions.Item>

              <Descriptions.Item label="Date Posted">{fmtDateTime(CreatedAt)}</Descriptions.Item>

              <Descriptions.Item label="Amount">{amount}</Descriptions.Item>
            </Descriptions>
            <Descriptions column={1}>
              <Descriptions.Item label="Status & Time of Status Change">
                {Status}, {fmtDateTime(UpdatedAt)}
              </Descriptions.Item>
              <Descriptions.Item label="One Time / Repeat">
                {ShareDetail.schedule.length > 1 ? "Repeat" : "One Time"}
              </Descriptions.Item>
              <Descriptions.Item label="Date">
                {ShareDetail.schedule.map((d) => moment(d).format("M/d")).join(", ")}
              </Descriptions.Item>
              <Descriptions.Item label="Type"> {ShareDetail.shareType} </Descriptions.Item>
              <Descriptions.Item label="Location Type"> {ShareDetail.locationType} </Descriptions.Item>
              <Descriptions.Item label="Caregiver Type"> {ShareDetail.giverType} </Descriptions.Item>
              <Descriptions.Item label="Time">
                {moment(ShareDetail.startTime, "HH:mm").format("h:mma")} -
                {moment(ShareDetail.endTime, "HH:mm").format("h:mma")}
              </Descriptions.Item>
              <Descriptions.Item label="Address">{ShareDetail?.addr}</Descriptions.Item>
              <Descriptions.Item label="Participating Children">
                {consumerChildren?.map(({ name }) => name).join(", ")}
              </Descriptions.Item>
              <Descriptions.Item label="Sender Notes"> {Notes}</Descriptions.Item>
              <Descriptions.Item label="Post Recipients">{recipients}</Descriptions.Item>
              <Descriptions.Item label="Counterparty"> {producer?.name}</Descriptions.Item>
              {/* <Descriptions.Item label="Extension request made by">-</Descriptions.Item>
            <Descriptions.Item label="Extension status">-</Descriptions.Item> */}
            </Descriptions>

            {services.length > 0 && (
              <div>
                <Descriptions title="Services"></Descriptions>
                <Table pagination={false} columns={columns} dataSource={services}></Table>
              </div>
            )}
          </div>
        )}

        {category === "uShare Request" && (
          <div>
            <Descriptions column={2}>
              <Descriptions.Item label="Category">{category}</Descriptions.Item>

              <Descriptions.Item label="Sender">
                {userActivityStore.data?.sender && (
                  <UserLink
                    userId={userActivityStore.data?.sender?.id}
                    name={userActivityStore.data?.sender?.name}
                  ></UserLink>
                )}
              </Descriptions.Item>

              <Descriptions.Item label="Date Posted">{fmtDateTime(CreatedAt)}</Descriptions.Item>

              <Descriptions.Item label="Amount">{amount}</Descriptions.Item>
            </Descriptions>

            <Descriptions column={1}>
              <Descriptions.Item label="Status & Time of Status Change">
                {Status}, {fmtDateTime(UpdatedAt)}
              </Descriptions.Item>
              <Descriptions.Item label="One Time / Repeat">
                {ShareDetail.schedule.length > 1 ? "Repeat" : "One Time"}
              </Descriptions.Item>
              <Descriptions.Item label="Description"> {ShareDetail.description}</Descriptions.Item>
              <Descriptions.Item label="Fare Type"> {ShareDetail.fareType}</Descriptions.Item>
              <Descriptions.Item label="Fare"> {ShareDetail.fare}</Descriptions.Item>

              <Descriptions.Item label="Date">
                {ShareDetail.schedule.map((d) => moment(d).format("M/D")).join(", ")}
              </Descriptions.Item>
              <Descriptions.Item label="Time">
                {moment(ShareDetail.startTime, "HH:mm").format("h:mma")} -
                {moment(ShareDetail.endTime, "HH:mm").format("h:mma")}
              </Descriptions.Item>
              <Descriptions.Item label="Location Type"> {ShareDetail.locationType} </Descriptions.Item>
              <Descriptions.Item label="Location"> {ShareDetail.addr} </Descriptions.Item>

              <Descriptions.Item label="Participating Children">
                {consumerChildren?.map(({ name }) => name).join(", ")}
              </Descriptions.Item>
              <Descriptions.Item label="Sender Notes"> {Notes}</Descriptions.Item>
              <Descriptions.Item label="Post Recipients">{recipients}</Descriptions.Item>
              <Descriptions.Item label="Cost Share">{CostAndSave?.cost?.total} </Descriptions.Item>
              <Descriptions.Item label="Counterparty">
                {producer && <UserLink userId={producer?.id} name={producer?.name}></UserLink>}
              </Descriptions.Item>
            </Descriptions>
            <div>
              <Space>
                {ShareDetail?.pics?.map((p) => (
                  <Image
                    style={{ maxWidth: 120, maxHeight: 120 }}
                    src={`https://${process.env.REACT_APP_CLOUDFRONT}.cloudfront.net/${p?.thumbnail}`}
                  ></Image>
                ))}
              </Space>
            </div>
            {services.length > 0 && (
              <div>
                <Descriptions title="Services"></Descriptions>
                <Table columns={columns} dataSource={services} pagination={false}></Table>
              </div>
            )}
          </div>
        )}

        {category === "uShare Offer" && (
          <div>
            <Descriptions column={2}>
              <Descriptions.Item label="Category">{category}</Descriptions.Item>

              <Descriptions.Item label="Sender">
                {userActivityStore.data?.sender && (
                  <UserLink
                    userId={userActivityStore.data?.sender?.id}
                    name={userActivityStore.data?.sender?.name}
                  ></UserLink>
                )}
              </Descriptions.Item>

              <Descriptions.Item label="Date Posted">{fmtDateTime(CreatedAt)}</Descriptions.Item>
            </Descriptions>

            <Descriptions column={1}>
              <Descriptions.Item label="Status & Time of Status Change">
                {Status}, {fmtDateTime(UpdatedAt)}
              </Descriptions.Item>

              <Descriptions.Item label="Description"> {ShareDetail.description}</Descriptions.Item>

              <Descriptions.Item label="Date">
                {ShareDetail.schedule.map((d) => moment(d).format("M/D")).join(", ")}
              </Descriptions.Item>
              <Descriptions.Item label="Time">
                {moment(ShareDetail.startTime, "HH:mm").format("h:mma")} -
                {moment(ShareDetail.endTime, "HH:mm").format("h:mma")}
              </Descriptions.Item>
              <Descriptions.Item label="Location Type"> {ShareDetail.locationType} </Descriptions.Item>
              <Descriptions.Item label="Location"> {ShareDetail.addr} </Descriptions.Item>

              <Descriptions.Item label="Sender Notes"> {Notes}</Descriptions.Item>
              <Descriptions.Item label="Post Recipients">{recipients}</Descriptions.Item>
            </Descriptions>

            <Descriptions title="Offer Items"></Descriptions>
            <Table columns={uShareOfferColumns} dataSource={ShareDetail.offeringDetails} pagination={false}></Table>

            {services.length > 0 && (
              <div>
                <Descriptions title="Services"></Descriptions>
                <Table columns={columns} dataSource={services} pagination={false}></Table>
              </div>
            )}
          </div>
        )}

        {category === "eduShare Offer" && (
          <div>
            <Descriptions column={2}>
              <Descriptions.Item label="Category">{category}</Descriptions.Item>

              <Descriptions.Item label="Sender">
                {userActivityStore.data?.sender && (
                  <UserLink
                    userId={userActivityStore.data?.sender?.id}
                    name={userActivityStore.data?.sender?.name}
                  ></UserLink>
                )}
              </Descriptions.Item>

              <Descriptions.Item label="Date Posted">{fmtDateTime(CreatedAt)}</Descriptions.Item>
            </Descriptions>

            <Descriptions column={1}>
              <Descriptions.Item label="Status & Time of Status Change">
                {Status}, {fmtDateTime(UpdatedAt)}
              </Descriptions.Item>

              <Descriptions.Item label="Description"> {ShareDetail.description}</Descriptions.Item>

              <Descriptions.Item label="Date">
                {ShareDetail.schedule.map((d) => moment(d).format("M/D")).join(", ")}
              </Descriptions.Item>
              <Descriptions.Item label="Time">
                {moment(ShareDetail.startTime, "HH:mm").format("h:mma")} -
                {moment(ShareDetail.endTime, "HH:mm").format("h:mma")}
              </Descriptions.Item>
              <Descriptions.Item label="Location"> {ShareDetail.addr} </Descriptions.Item>

              <Descriptions.Item label="Sender Notes"> {Notes}</Descriptions.Item>
              <Descriptions.Item label="Post Recipients">{recipients}</Descriptions.Item>
              <Descriptions.Item label="Share Link">
                <Typography.Paragraph
                  copyable
                >{`foomoo://splash?type=offerEduShareDetail&id=${OrderId}`}</Typography.Paragraph>
              </Descriptions.Item>
            </Descriptions>
            {ShareDetail.pic?.thumbnail ? (
              <Image
                style={{ maxWidth: 120, maxHeight: 120 }}
                src={`https://${process.env.REACT_APP_CLOUDFRONT}.cloudfront.net/${ShareDetail.pic?.thumbnail}`}
              ></Image>
            ) : null}

            <Descriptions
              title="Promote"
              column={2}
              extra={[
                !banner && (
                  <Button
                    type="primary"
                    size="small"
                    onClick={() =>
                      bannerEditStore.show({
                        type: "EduShareOffer",
                        eduShareOffer: {
                          id: userActivityStore.data.OrderId,
                        },
                      })
                    }
                  >
                    Add
                  </Button>
                ),
                banner && (
                  <Button type="primary" size="small" onClick={() => bannerEditStore.show(banner)}>
                    Edit
                  </Button>
                ),
                banner && (
                  <Button type="default" size="small" danger onClick={() => userActivityStore.delBanner(banner)}>
                    Delete
                  </Button>
                ),
              ]}
            >
              {banner && (
                <Descriptions.Item label="Title" span={2}>
                  {banner?.eduShareOffer?.title}
                </Descriptions.Item>
              )}
              {banner && <Descriptions.Item label="Price">{banner?.eduShareOffer?.price}</Descriptions.Item>}
              {banner && (
                <Descriptions.Item label="Original Price">{banner?.eduShareOffer?.originalPrice}</Descriptions.Item>
              )}
              {banner && (
                <Descriptions.Item label="Summary" span={2}>
                  <span>
                    {(banner?.eduShareOffer?.summary || "").split("\n").map((x) => (
                      <div>{x}</div>
                    ))}
                  </span>
                </Descriptions.Item>
              )}
            </Descriptions>

            {services.length > 0 && (
              <div>
                <Descriptions title="Services"></Descriptions>
                <Table columns={columns} dataSource={services} pagination={false}></Table>
              </div>
            )}
          </div>
        )}

        {category === "Carpool Offer" && (
          <div>
            <Descriptions column={2}>
              <Descriptions.Item label="Category">{category}</Descriptions.Item>

              <Descriptions.Item label="Sender">
                {userActivityStore.data?.sender && (
                  <UserLink
                    userId={userActivityStore.data?.sender?.id}
                    name={userActivityStore.data?.sender?.name}
                  ></UserLink>
                )}
              </Descriptions.Item>

              <Descriptions.Item label="Date Posted">{fmtDateTime(CreatedAt)}</Descriptions.Item>

              <Descriptions.Item label="Amount">
                {/* {fmtMoney2(CostAndSave?.cost?.per || CostAndSave?.cost?.total, "-", "/trip")} */}
              </Descriptions.Item>
            </Descriptions>

            <Descriptions column={1}>
              <Descriptions.Item label="Status & Time of Status Change">
                {Status}, {fmtDateTime(UpdatedAt)}
              </Descriptions.Item>
              <Descriptions.Item label="One Time / Repeat">
                {ShareDetail.schedule.length > 1 ? "Repeat" : "One Time"}
              </Descriptions.Item>
              <Descriptions.Item label="Date">
                {ShareDetail.schedule.map((d) => moment(d).format("M/D")).join(", ")}
              </Descriptions.Item>
              <Descriptions.Item label="Type"> {ShareDetail.type} </Descriptions.Item>

              {ShareDetail.type === "Pick up From" && (
                <div>
                  <Descriptions.Item label="Start Time">
                    {moment(ShareDetail.startTime, "HH:mm").format("h:mma")}
                  </Descriptions.Item>
                  <Descriptions.Item label="Pick up Address">
                    {ShareDetail?.pickUpPlace?.displayAddress}
                  </Descriptions.Item>
                </div>
              )}
              {ShareDetail.type === "Going To" && (
                <div>
                  <Descriptions.Item label="End Time">
                    {moment(ShareDetail.endTime, "HH:mm").format("h:mma")}
                  </Descriptions.Item>
                  <Descriptions.Item label="Drop off Address">
                    {ShareDetail?.dropOffPlace?.displayAddress}
                  </Descriptions.Item>
                </div>
              )}
              {/*  {ShareDetail.type == "Round Trip" && (
                <Descriptions.Item label="Time To Return">
                  {moment(ShareDetail.timeToReturn, "HH:mm").format("h:mma")}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Pick up Address"> {ShareDetail?.pickUpPlace?.displayAddress}</Descriptions.Item> */}

              <Descriptions.Item label="Participating Children">
                {consumerChildren?.map(({ name }) => name).join(", ")}
              </Descriptions.Item>
              <Descriptions.Item label="Sender Notes"> {Notes}</Descriptions.Item>
              <Descriptions.Item label="Post Recipients">{recipients}</Descriptions.Item>
              <Descriptions.Item label="Cost Share">{CostAndSave?.cost?.total} </Descriptions.Item>
              {/* <Descriptions.Item label="Counterparty">
                {producer && <UserLink userId={producer?.id} name={producer?.name}></UserLink>}
              </Descriptions.Item> */}
            </Descriptions>

            {services.length > 0 && (
              <div>
                <Descriptions title="Services"></Descriptions>
                <Table columns={columns} dataSource={services} pagination={false}></Table>
              </div>
            )}
          </div>
        )}

        {!done.includes(category) && (
          <div>
            <Descriptions column={2}>
              <Descriptions.Item label="Category">{category}</Descriptions.Item>

              <Descriptions.Item label="Title">{title}</Descriptions.Item>
              <Descriptions.Item label="Sender">
                {userActivityStore.data?.sender && (
                  <UserLink
                    userId={userActivityStore.data?.sender?.id}
                    name={userActivityStore.data?.sender?.name}
                  ></UserLink>
                )}
              </Descriptions.Item>

              <Descriptions.Item label="Date Posted">{fmtDateTime2(CreatedAt)}</Descriptions.Item>

              <Descriptions.Item label="Status">{Status}</Descriptions.Item>
              <Descriptions.Item label="Amount">{amount}</Descriptions.Item>
            </Descriptions>
          </div>
        )}
        <EduShareOfferBannerDlg
          onDone={() => {
            userActivityStore.refresh();
          }}
        />
      </Card>
    </Modal>
  );
});
